import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  InputBase,
  InputContainer,
  InputLabelContainer,
  InputSelectTrailingContainer,
  ListItem,
  ListItemMainContent,
  ListItemParagraph,
  InputSelect,
  InputMultiSelectInput,
  ListItemTrailing,
  InputSelectSearchableContent,
  InputSelectSearchableTrigger,
  InputSelectSearchableItem,
} from '@sweetspot/scramble-ds'
import { Check, Divider, Tag, Tooltip } from '@sweetspot/scramble-ds/atoms'
import {
  PeriodDayGroup,
  PeriodFormSelectFieldOption,
  weekDaysGrouped,
  weekDaysOptions,
} from '@sweetspot/club-portal-legacy/pages/Settings/components/AvailabilityAndPricing/types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'

type DaysToApplyProps = {
  values: Array<PeriodDayGroup | string>
  onChange: Dispatch<SetStateAction<Array<PeriodDayGroup | string>>>
}

const DaysToApply = ({ values, onChange }: DaysToApplyProps) => {
  const { t } = useTranslation()

  const selectedOptions: PeriodFormSelectFieldOption[] = useMemo(() => {
    if (!values) return []
    if (values.includes(PeriodDayGroup.EVERY_DAY)) {
      return [...weekDaysOptions]
    }
    if (values.includes(PeriodDayGroup.WEEKDAY)) {
      const weekendValues = values.filter(
        (value) =>
          value === PeriodDayGroup.WEEKEND ||
          weekDaysGrouped[PeriodDayGroup.WEEKEND].includes(value)
      )
      return weekDaysOptions.filter(
        (option) =>
          weekendValues.includes(option.value) ||
          option.value === PeriodDayGroup.WEEKDAY ||
          weekDaysGrouped[PeriodDayGroup.WEEKDAY].includes(option.value)
      )
    }
    if (values.includes(PeriodDayGroup.WEEKEND)) {
      const weekdayValues = values.filter(
        (value) =>
          value === PeriodDayGroup.WEEKDAY ||
          weekDaysGrouped[PeriodDayGroup.WEEKDAY].includes(value)
      )
      return weekDaysOptions.filter(
        (option) =>
          weekdayValues.includes(option.value) ||
          option.value === PeriodDayGroup.WEEKEND ||
          weekDaysGrouped[PeriodDayGroup.WEEKEND].includes(option.value)
      )
    }
    return weekDaysOptions.filter((option) => values?.includes(option.value))
  }, [values])

  const isWeekday = useCallback(
    (day: string) => weekDaysGrouped[PeriodDayGroup.WEEKDAY].includes(day),
    []
  )

  const simplifyDays = useCallback((selectedDayGroup: PeriodDayGroup, values: string[]) => {
    let newValues: string[] = [...values]
    newValues = newValues.filter((value) => !weekDaysGrouped[selectedDayGroup].includes(value))
    if (!newValues.includes(selectedDayGroup)) {
      newValues.push(selectedDayGroup)
    }
    return newValues
  }, [])

  const handleOnChange = useCallback(
    (values: string[]) => {
      let newValues: string[] = [...values]

      const hasWeekday = values.includes(PeriodDayGroup.WEEKDAY)
      const hasWeekend = values.includes(PeriodDayGroup.WEEKEND)

      // if all days are selected,
      // send only every_day
      if (values.includes(PeriodDayGroup.EVERY_DAY)) {
        onChange([PeriodDayGroup.EVERY_DAY])
        return
      }

      // if weekdays is selected,
      // or all single weekdays are selected
      // remove unecessary single weekdays
      if (
        hasWeekday ||
        weekDaysGrouped[PeriodDayGroup.WEEKDAY].every((day) => values.includes(day))
      ) {
        newValues = simplifyDays(PeriodDayGroup.WEEKDAY, newValues)
      }

      // if weekend is selected,
      // or all single weekend days are selected
      // remove unecessary single weekend days
      if (
        hasWeekend ||
        weekDaysGrouped[PeriodDayGroup.WEEKEND].every((day) => values.includes(day))
      ) {
        newValues = simplifyDays(PeriodDayGroup.WEEKEND, newValues)
      }

      // if all days are selected in newValues,
      // send only every_day
      if (
        newValues.includes(PeriodDayGroup.WEEKDAY) &&
        newValues.includes(PeriodDayGroup.WEEKEND)
      ) {
        newValues = [PeriodDayGroup.EVERY_DAY]
      }

      onChange(newValues)
    },
    [onChange, simplifyDays]
  )

  const handleAddSelectedDay = useCallback(
    (day: string) => {
      handleOnChange([...values, day])
    },
    [handleOnChange, values]
  )

  const handleRemoveSelectedDay = useCallback(
    (day: string) => {
      let newValues = selectedOptions
        .map((option) => option.value)
        .filter((option) => option !== day && option !== PeriodDayGroup.EVERY_DAY)
      if (isWeekday(day)) {
        newValues = newValues.filter((option) => option !== PeriodDayGroup.WEEKDAY)
        handleOnChange(newValues)
      } else {
        newValues = newValues.filter((option) => option !== PeriodDayGroup.WEEKEND)
        handleOnChange(newValues)
      }
    },
    [handleOnChange, isWeekday, selectedOptions]
  )

  const handleSelectEveryDayCheckChange = useCallback(
    (checked: string | boolean) => {
      if (checked) {
        handleOnChange([PeriodDayGroup.EVERY_DAY])
      } else {
        handleOnChange([])
      }
    },
    [handleOnChange]
  )

  const handleSelectWeekdaysCheckChange = useCallback(
    (checked: string | boolean) => {
      if (checked) {
        handleOnChange([...values, PeriodDayGroup.WEEKDAY])
      } else {
        const newValues = selectedOptions
          .map((option) => option.value)
          .filter(
            (option) =>
              option !== PeriodDayGroup.WEEKDAY &&
              option !== PeriodDayGroup.EVERY_DAY &&
              !weekDaysGrouped[PeriodDayGroup.WEEKDAY].includes(option)
          )
        handleOnChange(newValues)
      }
    },
    [handleOnChange, values, selectedOptions]
  )

  const handleSelectWeekendCheckChange = useCallback(
    (checked: string | boolean) => {
      if (checked) {
        handleOnChange([...values, PeriodDayGroup.WEEKEND])
      } else {
        const newValues = selectedOptions
          .map((option) => option.value)
          .filter(
            (option) =>
              option !== PeriodDayGroup.WEEKEND &&
              option !== PeriodDayGroup.EVERY_DAY &&
              !weekDaysGrouped[PeriodDayGroup.WEEKEND].includes(option)
          )
        handleOnChange(newValues)
      }
    },
    [handleOnChange, values, selectedOptions]
  )

  const handleDayCheckChange = useCallback(
    (checked: string | boolean, day: string) => {
      if (day === PeriodDayGroup.EVERY_DAY) {
        return handleSelectEveryDayCheckChange(checked)
      }
      if (day === PeriodDayGroup.WEEKDAY) {
        return handleSelectWeekdaysCheckChange(checked)
      }
      if (day === PeriodDayGroup.WEEKEND) {
        return handleSelectWeekendCheckChange(checked)
      }
      if (checked) {
        handleAddSelectedDay(day)
      } else {
        handleRemoveSelectedDay(day)
      }
    },
    [
      handleRemoveSelectedDay,
      handleSelectEveryDayCheckChange,
      handleSelectWeekdaysCheckChange,
      handleAddSelectedDay,
      handleSelectWeekendCheckChange,
    ]
  )

  return (
    <div className="flex items-end justify-between">
      <InputSelect withSearch>
        <InputBase>
          <InputLabelContainer>{t('dateTime.words.days')}</InputLabelContainer>
          <InputSelectSearchableTrigger asChild>
            <InputContainer className="w-full">
              <InputMultiSelectInput
                className="text-content-base z-50 min-h-8 border-[1px] py-1"
                placeholder={t('words.select')}
              >
                {values.map((option) => {
                  const day = weekDaysOptions.find((day) => day.value === option)
                  return (
                    <Tag.Root key={option} size="large" variant="primary" className="gap-2 px-2">
                      {t(day?.label || '')}
                      {values.length > 1 && (
                        <i
                          className="fa-solid fa-close cursor-pointer"
                          role="button"
                          aria-label="Remove tag"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDayCheckChange(false, day?.value || '')
                          }}
                        />
                      )}
                    </Tag.Root>
                  )
                })}
              </InputMultiSelectInput>
              <InputSelectTrailingContainer className="right-2" />
            </InputContainer>
          </InputSelectSearchableTrigger>
        </InputBase>
        <InputSelectSearchableContent className="max-h-fit">
          {weekDaysOptions.map((option, index) => (
            <>
              <InputSelectSearchableItem className={'px-4'} key={option.value} value={option.value}>
                <ListItem className="text-content-base font-regular h-full w-full justify-between gap-2">
                  <ListItemMainContent className="flex-none justify-center">
                    <ListItemParagraph className="text-content-base">
                      {t(option.label)}
                    </ListItemParagraph>
                  </ListItemMainContent>
                  <ListItemTrailing onClick={(e) => e.stopPropagation()}>
                    <Check
                      checked={selectedOptions.some((day) => day.value === option.value)}
                      onCheckedChange={(checked) => {
                        handleDayCheckChange(checked, option.value)
                      }}
                    />
                  </ListItemTrailing>
                </ListItem>
              </InputSelectSearchableItem>
              {index === 2 && <Divider />}
            </>
          ))}
        </InputSelectSearchableContent>
      </InputSelect>
      <Tooltip.Provider key="datesTooltip">
        <Tooltip.Root>
          <React.Fragment key="datesTooltip">
            <Tooltip.Trigger asChild>
              <div className="flex h-8 w-8 items-center justify-end p-1">
                <FontAwesomeIcon icon={faCircleInfo} className="text-text-subtle" />
              </div>
            </Tooltip.Trigger>
            <Tooltip.Content>
              <div className="flex w-[263px] flex-col gap-4 py-2">
                <p className="text-content-sm text-text-light font-bold">
                  {t('dateTime.words.day_other')}
                </p>
                <p className="text-content-sm text-text-light font-regular">
                  {t('sentences.adjustmentDaysToApplyTooltipDescription')}
                </p>
              </div>
              <Tooltip.Arrow />
            </Tooltip.Content>
          </React.Fragment>
        </Tooltip.Root>
      </Tooltip.Provider>
    </div>
  )
}

export default DaysToApply
